import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { filter, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from '../core/auth.service';
import { ApiService } from '../core/api.service';
import { NavDensity, NavMode, SidenavPrefsService } from '../core/sidenav-prefs.service';
import { ThemeService } from '../core/theme.service';
import { DialogsService } from '../shared/dialogs';
import { folioTone } from '../shared/folio-tone';

interface NavItem { link: string; label: string; icon: string; roles: string[]; group: string; }

const NAV: NavItem[] = [
  { link: '/inicio', label: 'Inicio', icon: 'home', roles: ['Administracion', 'Consulta'], group: 'Operación' },
  { link: '/socios', label: 'Socios', icon: 'groups', roles: ['Administracion', 'Consulta', 'Censista'], group: 'Operación' },
  { link: '/censos', label: 'Censos', icon: 'pets', roles: ['Administracion', 'Censista'], group: 'Operación' },
  { link: '/estado-cuenta', label: 'Estado de cuenta', icon: 'receipt_long', roles: ['Administracion', 'Consulta'], group: 'Operación' },
  { link: '/bafi/turno', label: 'Turno BaFi', icon: 'scale', roles: ['Administracion', 'Bafi'], group: 'Báscula' },
  { link: '/bafi/folios', label: 'Folios', icon: 'confirmation_number', roles: ['Administracion', 'Bafi', 'Consulta'], group: 'Báscula' },
  { link: '/mantenimiento', label: 'Mantenimiento', icon: 'build', roles: ['Administracion', 'Mantenimiento'], group: 'Campo' },
  { link: '/pozos', label: 'Pozos', icon: 'water_drop', roles: ['Administracion', 'Lecturista'], group: 'Campo' },
  { link: '/cuotas', label: 'Tarifas y cuotas', icon: 'payments', roles: ['Administracion'], group: 'Administración' },
  { link: '/avisos', label: 'Avisos', icon: 'campaign', roles: ['Administracion'], group: 'Administración' },
  { link: '/administracion', label: 'Administración', icon: 'admin_panel_settings', roles: ['Administracion'], group: 'Administración' },
];

const TITLES: Record<string, { title: string; subtitle: string }> = {
  '/inicio': { title: 'Hoy', subtitle: 'Nuevas Granjas Fátima' },
  '/socios': { title: 'Socios y lotes', subtitle: 'Altas, calles y arrendatarios' },
  '/censos': { title: 'Censo de campo', subtitle: 'Un censo por lote y por campaña' },
  '/cuotas': { title: 'Tarifas y cuotas', subtitle: 'Sociedad, energética y semáforo' },
  '/estado-cuenta': { title: 'Estado de cuenta', subtitle: 'Cargos, pagos y recibo de agua' },
  '/bafi/turno': { title: 'Turno de báscula', subtitle: 'Apertura, caja y entrega' },
  '/bafi/folios': { title: 'Folios', subtitle: 'Semana 43 · 21 de octubre de 2026' },
  '/mantenimiento': { title: 'Mantenimiento', subtitle: 'Órdenes y salida de material' },
  '/pozos': { title: 'Pozos', subtitle: 'Lectura con foto' },
  '/avisos': { title: 'Avisos', subtitle: 'WhatsApp y acuse' },
  '/administracion': { title: 'Administración', subtitle: 'Doble factor, presencia y bitácora' },
};

@Component({
  selector: 'app-shell',
  imports: [
    RouterOutlet, RouterLink, RouterLinkActive, FormsModule,
    MatSidenavModule, MatToolbarModule, MatButtonModule, MatIconModule,
    MatListModule, MatMenuModule, MatTooltipModule, MatFormFieldModule, MatInputModule,
  ],
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.scss',
})
export class ShellComponent implements OnInit, OnDestroy {
  readonly auth = inject(AuthService);
  readonly theme = inject(ThemeService);
  readonly nav = inject(SidenavPrefsService);
  readonly envLabel = environment.label;
  readonly version = environment.version;
  private readonly router = inject(Router);
  private readonly api = inject(ApiService);
  private readonly dialogs = inject(DialogsService);
  private readonly breakpoint = inject(BreakpointObserver);

  readonly items = computed(() => NAV.filter((item) => item.roles.includes(this.auth.role())));
  readonly groups = computed(() => {
    const order = ['Operación', 'Báscula', 'Campo', 'Administración'];
    return order
      .map((label) => ({ label, items: this.items().filter((item) => item.group === label) }))
      .filter((group) => group.items.length > 0);
  });
  readonly live = signal('');
  readonly online = signal('');
  readonly locked = signal(sessionStorage.getItem('cografa:locked') === '1');
  readonly unlockError = signal('');
  readonly pageTitle = signal('COGRAFA');
  readonly pageSubtitle = signal('Nuevas Granjas Fátima');
  readonly titlePulse = signal(false);
  readonly badges = signal<Record<string, string>>({});
  readonly folioAlert = signal(false);
  readonly mobileOpen = signal(false);
  readonly initial = computed(() => (this.auth.user()?.displayName ?? 'C').trim().charAt(0).toUpperCase());
  readonly isHandset = toSignal(this.breakpoint.observe(Breakpoints.Handset).pipe(map((r) => r.matches)), { initialValue: false });
  readonly sidenavOpened = computed(() => (this.isHandset() ? this.mobileOpen() : this.nav.opened()));
  readonly drawerMode = computed(() => (this.isHandset() || this.nav.mode() === 'overlay' ? 'over' : 'side'));
  readonly navCompact = computed(() => !this.isHandset() && this.nav.density() === 'compact');
  unlockPassword = '';
  private hub?: HubConnection;

  ngOnInit(): void {
    document.documentElement.classList.toggle('cografa-field', this.auth.isField());
    this.syncTitle(this.router.url);
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
      this.syncTitle((event as NavigationEnd).urlAfterRedirects);
    });
    const token = localStorage.getItem('cografa:token');
    if (!token) return;
    this.hub = new HubConnectionBuilder().withUrl('/hubs/ops?access_token=' + token).withAutomaticReconnect().build();
    this.hub.on('ops', (msg: { title?: string }) => {
      this.live.set(msg.title ?? 'Actualización');
      this.loadFolioBadge();
    });
    this.hub.on('presence', (names: string[]) => this.online.set(names.join(', ')));
    void this.hub.start().catch(() => this.live.set(''));
    this.loadFolioBadge();
    if (this.router.url === '/' || this.router.url === '/inicio') {
      const home = this.auth.home();
      if (home !== '/inicio') void this.router.navigateByUrl(home);
    }
  }

  ngOnDestroy(): void { void this.hub?.stop(); }

  toggleNav(): void {
    if (this.isHandset()) this.mobileOpen.update((open) => !open);
    else this.nav.patch({ opened: !this.nav.opened() });
  }

  onOpened(opened: boolean): void {
    if (this.isHandset()) this.mobileOpen.set(opened);
    else if (this.nav.mode() === 'docked') this.nav.patch({ opened });
  }

  closeMobile(): void {
    if (this.isHandset()) this.mobileOpen.set(false);
  }

  setDensity(density: NavDensity): void { this.nav.patch({ density }); }
  setMode(mode: NavMode): void { this.nav.patch({ mode }); }

  openHelp(): void {
    this.dialogs.help(this.pageTitle(), this.helpText());
  }

  helpText(): string {
    const path = this.router.url.split('?')[0];
    if (path.includes('socios')) return 'El alta pide identificador, nombre y WhatsApp. El arrendatario recibe copia del aviso; la deuda sigue a nombre del socio.';
    if (path.includes('censos')) return 'Un censo por lote. Si el GPS de la oficina no alcanza 50 m, captura latitud y longitud a mano. La foto se elige o se toma con la cámara y se guarda en el servidor.';
    if (path.includes('estado-cuenta')) return 'La sociedad corta el día 10. La energética suma el tipo de lote y los animales, con mínimo de 200, y el recibo de agua se paga antes del último día del mes. Dos meses seguidos sin pagar suspenden el suministro.';
    if (path.includes('bafi/turno')) return 'La entrega pide usuario y contraseña de otra persona. Si alguien más tiene el turno abierto, esta pantalla queda en lectura.';
    if (path.includes('bafi/folios')) return 'Amarillo desde 15 folios y rojo desde 5, o si el consecutivo calculado no coincide con el reportado. Conciliar pide cómo se verificó. Un rango que empieza en 0 se muestra como 0.';
    if (path.includes('mantenimiento')) return 'La salida de manguera usa una cantidad negativa. Al cerrar la orden se anotan destino, mano de obra e indirectos.';
    if (path.includes('pozos')) return 'La lectura exige foto: se toma con la cámara o se elige del equipo. Un salto anormal se manda a revisión y no sustituye la lectura anterior.';
    if (path.includes('cuotas')) return 'La sociedad es 250 por lote y corta el día 10. La energética suma el tipo de lote y los animales, con mínimo de 200. La extraordinaria es 9,000 una vez por lote.';
    if (path.includes('avisos')) return 'Primero se arma el texto y se abre WhatsApp. El estado de entrega se registra como Generado, Abierto o Entregado.';
    if (path.includes('administracion')) return 'El doble factor es solo de esta cuenta. Congelar el mes guarda una foto de los indicadores. La bitácora no se edita.';
    return 'COGRAFA separa la caja de báscula, el fondo de caja chica y las cuotas de socios. El domingo no hay turno.';
  }

  lock(): void {
    sessionStorage.setItem('cografa:locked', '1');
    this.locked.set(true);
  }

  unlock(): void {
    const name = this.auth.user()?.userName ?? '';
    this.auth.login(name, this.unlockPassword).subscribe({
      next: () => {
        sessionStorage.removeItem('cografa:locked');
        this.locked.set(false);
        this.unlockPassword = '';
        this.unlockError.set('');
      },
      error: () => this.unlockError.set('La contraseña no coincide.'),
    });
  }

  private syncTitle(url: string): void {
    const path = url.split('?')[0];
    const entry = TITLES[path] ?? { title: 'COGRAFA', subtitle: 'Nuevas Granjas Fátima' };
    this.titlePulse.set(false);
    this.pageTitle.set(entry.title);
    this.pageSubtitle.set(entry.subtitle);
    setTimeout(() => this.titlePulse.set(true));
  }

  private loadFolioBadge(): void {
    if (!['Administracion', 'Bafi', 'Consulta'].includes(this.auth.role())) return;
    this.api.get<any[]>('folios').subscribe({
      next: (rows) => {
        const tones = rows.map((row) => folioTone(row));
        const alert = tones.filter((tone) => tone !== 'Verde').length;
        this.folioAlert.set(tones.includes('Rojo'));
        this.badges.set(alert ? { '/bafi/folios': String(alert) } : {});
      },
      error: () => this.badges.set({}),
    });
  }
}
